<template>
  <div id="choiceItemEdit">
    <div class="container">

      <ChoiceNavigation></ChoiceNavigation>

      <div class="page-title" v-if="data">
        <span class="name" v-if="!data.name.fr && !data.name.en">&nbsp;</span>
        <span class="name" v-if="data.name.fr || data.name.en">{{ $to(data.name) }}</span>
        <button class="save btn btn-primary" @click="save" v-if="$store.getters.loggedUserCanSaveInventory">
          {{ $t("save") }}
        </button>
      </div>

      <Tabs>
        <TabSection :title="$t('items.information')" v-if="data">
          <div class="columns">
            <div class="column">
              <div class="group">
                <LocalizedInput :title="$t('name')" :translate="data.name" />
              </div>

              <div class="group">
                <LocalizedInput :title="$t('description')" :translate="data.description"
                  inputClass="editable-description" />
              </div>

              <div class="group" v-if="data.kitchen && data.kitchen.description">
                <LocalizedInput :max-size="20" :title="$t('items.kitchen_description')"
                  :translate="data.kitchen.description" />
              </div>

              <div class="group">
                <label>
                  <span>{{ $t("items.tags") }}</span>
                  <Tooltip :text="$t('items.choose_tag')" />
                </label>
                <MultiSelect :options="tags" :selected="data.tags" :trackByAttribute="'value'"
                  @changed="data.tags = $event" />
              </div>

              <div class="group">
                <label>
                  <span>{{ $t('note') }} ({{ data.note ? data.note.length : 0 }}/{{ noteMaxLength }})</span>
                  <Tooltip :text="$t('note_tooltip')"></Tooltip>
                </label>
                <input type="text" v-model="data.note" :maxlength="noteMaxLength">
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>{{ $t('items.hide_in_cart') }}</label>
                <select v-model="data.hideInCart">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>

              <div class="group">
                <ImageUpload identifier="choiceItemImage" :object="data.image" @changed="data.image = $event"
                  :maxSizeInMb="0.8"></ImageUpload>
              </div>
            </div>
          </div>

          <InventoryUsagePanel v-if="$route.query.id" type="choice-item" :id="$route.query.id"></InventoryUsagePanel>
        </TabSection>

        <TabSection :title="$t('items.payment_information')">
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t('items.taxable') }}</label>
                <select v-model="data.taxable">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('items.out_of_stock') }}</label>
                <select v-model="data.outOfStock" class="editable-outOfStock">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
            </div>
            <div class="column">
              <div class="group">
                <TipsExternalId/>
                <label>{{ $t('external_id') }}</label>
                <ExternalIDInput v-if="inventory" v-model="data.externalId" :inventory="inventory.id" :sync-item="data"
                                 input-class="editable-externalId"/>
              </div>
              <div class="group" v-if="$store.getters.loggedUserHasBranchAdmin">
                <label>{{ $t('items.external_rules') }}</label>
                <input type="text" v-model="data.externalRules">
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('items.prices_configuration')" v-if="ready">
          <ItemPrices :item="data"></ItemPrices>
        </TabSection>

        <TabSection :title="$t('items.modifier_groups')">
          <div class="choice-explanation">{{ $t('choices.modifier_group_explanation') }}</div>
          <div class="tab-content">
            <ListMultiSelect :bank="modifierGroups" :bankTitle="$t('items.modifier_group_bank')"
              :selected="data.modifierGroups" :selectedTitle="$t('items.selected_modifier_groups')" />
          </div>
        </TabSection>

        <TabSection :title="$t('choices.product_combos')" v-if="data">
          <div class="choice-explanation">{{ $t('choices.product_combo_explanation') }}
            <b>{{ $t('choices.product_combo_warning') }}</b>
          </div>
          <div class="tab-content">
            <ListMultiSelect :bank="choices" :bankTitle="$t('items.choice_bank')" :selected="data.choices"
              :selectedTitle="$t('items.selected_choices')" />
          </div>
        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="$store.getters.loggedUserCanSaveInventory">
        <button class="btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import ChoiceNavigation from '@/components/choices/Navigation.vue';
  import ListMultiSelect from '@/components/tools/ListMultiSelect.vue';
  import { mapActions } from "vuex";
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import MultiSelect from '@/components/tools/MultiSelect.vue';
  import ImageUpload from "@/components/tools/ImageUpload.vue";
  import ItemPrices from "@/components/inventory/ItemPrices.vue";
  import ExternalIDInput from "@/components/inventory/ExternalIDInput.vue";
  import TipsExternalId from "@/components/tips/TipsExternalId.vue";

  export default {
    name: "choiceItemEdit",

    components: { ChoiceNavigation, ListMultiSelect, LocalizedInput, MultiSelect, ImageUpload, ItemPrices,
      ExternalIDInput, TipsExternalId },

    data() {
      return {
        inventory: null,
        choices: [],
        modifierGroups: [],
        tags: [],
        currencySymbol: '$',
        data: {
          id: null,
          name: { fr: "" },
          description: { fr: "" },
          kitchen: {
            description: { fr: "", en: "" },
          },
          priceSchemes: [],
          externalId: null,
          externalRules: "{}",
          outOfStock: false,
          hideInCart: false,
          image: null,
          note: undefined,
          revenueCenter: {
            prices: {}
          },
          choices: [],
          modifierGroups: [],
          tags: []
        },
        ready: false,
        noteMaxLength: 16
      }
    },

    computed: {
      inventoryReference() {
        return this.$store.state.contextCompany ? this.$store.state.contextCompany.nameCanonical : this.inventory.id;
      }
    },

    validate: {
      "data.name.fr": {
        required: false
      }
    },

    async mounted() {
      let choiceItemId = this.$route.query.id;
      this.inventory = await this.$store.getters.getContextInventory;
      this.currencySymbol = await this.$store.getters.getContextCurrencySymbol;
      this.data.priceSchemes = this.inventory.deliveryMethods.map(method => ({ method: method, unitPrice: null }));

      this.$store.commit('showSpinner', true);
      Promise
        .all([
          this.loadTags(),
          this.getChoices({ inventoryId: this.inventoryReference }),
          this.getModifierGroups({ inventoryId: this.inventoryReference }),
          choiceItemId
            ? this.getChoiceItem({ inventoryId: this.inventoryReference, id: choiceItemId })
            : Promise.resolve()
        ])
        .then(async ([tags, choices, modifierGroups, choiceItem]) => {
          this.tags = tags;
          this.choices = choices;//.filter(c => !!!c.slaveItems.find(i => i.toString() === choiceItemId)); TODO filter out parent combo
          this.modifierGroups = modifierGroups;

          if (choiceItemId) {
            this.data.id = choiceItem.id;
            this.data.name = choiceItem.name;
            this.data.description = choiceItem.description;
            this.data.priceSchemes = choiceItem.priceSchemes;
            this.data.externalId = choiceItem.externalId;
            this.data.externalRules = choiceItem.externalRules ? JSON.stringify(choiceItem.externalRules) : JSON.stringify({});
            this.data.hideInCart = choiceItem.hideInCart || false;
            this.data.image = choiceItem.image;
            this.data.note = choiceItem.note;
            this.data.outOfStock = choiceItem.outOfStock;
            this.data.taxable = choiceItem.taxable;
            this.data.kitchen = choiceItem.kitchen || {};
            this.data.revenueCenter = choiceItem.revenueCenter || { prices: {} };
            if (!this.data.kitchen.description) {
              this.data.kitchen.description = { fr: "", en: "" };
            }
            this.data.tags = this.tags.filter(t => choiceItem.tags.includes(t.value));
            this.data.choices = this.choices.filter(c => choiceItem.choices.includes(c.id));
            this.data.modifierGroups = this.modifierGroups.filter(c => choiceItem.modifierGroups.includes(c.id));
            await this.loadItemImage();
          }
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.ready = true;
          this.$store.commit('showSpinner', false);

          if (this.$store.getters.isBranchContextInventory) {
            window.lockFields(this.$store.getters.getOverloadableFields);
          } else if (!this.$store.getters.loggedUserCanModifyInventory) {
            window.lockFields();
          }
        })
    },

    watch: {
      "$store.getters.isBranchContextInventory"(newValue) {
        if (newValue) {
          window.lockFields(this.$store.getters.getOverloadableFields);
        }
      }
    },

    methods: {

      ...mapActions(['getChoiceItem', "getChoices", "getModifierGroups", "saveChoiceItem", "createChoiceItem", "getImage", "saveImage", "getManyInventoryEntity"]),

      async save() {
        if (!this.$validate.valid()) {
          toast(this.$t("branch.error.required"), "warning");
          this.$validate.validateAllOnce();
          return;
        }
        let copy = JSON.parse(JSON.stringify(this.data));
        copy = await this._formatImageBeforeSave(copy);

        if (this._findFirstInvalidChildCombo(copy.choices)) {
          toast(this.$t("modifiers.error.invalid_combo_relationship") +
            "<br><b>" +
            this.$to(this._findFirstInvalidChildCombo(copy.choices)) + "</b>", "error");
          return;
        }

        copy.modifierGroups = copy.modifierGroups.map(c => c.id);
        copy.choices = copy.choices.map(c => c.id);
        copy.tags = copy.tags.map(t => t.value);

        if (this.data.id) {
          let response = await this.saveChoiceItem({ inventoryId: this.inventoryReference, itemId: this.data.id, data: copy });
          if (!response) {
            toast(this.$t("modifiers.error.save"), "error");
            this.$store.commit("showSpinner", false);
            return;
          }
        } else {
          let response = await this.createChoiceItem({ inventoryId: this.inventoryReference, data: copy });
          if (!response) {
            toast(this.$t("modifiers.error.save"), "error");
            this.$store.commit("showSpinner", false);
            return;
          } else {
            this.data.id = response.id;
            toast(this.$t("modifiers.success"), "success");
          }
        }
        this.$router.push(this.$router.buildContext("/choice-items"));
        this.$store.commit("showSpinner", false);
      },

      async _formatImageBeforeSave(obj) {
        obj.externalRules = obj.externalRules ? JSON.parse(obj.externalRules) : JSON.stringify({});

        if (obj.image && obj.image.url.startsWith("data:")) {
          let image = await this.saveImage({ inventoryId: this.inventoryReference, image: getImageData(obj.image) });
          obj.image = image ? image.id : null;
        } else if (obj.image && obj.image.id) {
          obj.image = obj.image.id;
        } else if (obj.image && obj.image.deleted) {
          obj.image = null;
        }
        return obj;
      },
      async loadItemImage() {
        if (this.data.image) {
          let image = await this.getImage({ inventoryId: this.inventoryReference, imageId: this.data.image });
          this.data.image = image;
        }
      },

      _findFirstInvalidChildCombo(childCombos) {
        let choiceItemId = this.$route.query.id;
        for (let childCombo of childCombos) {
          let comboDetail = this.choices.find(c => c.id == childCombo.id);
          if (comboDetail && comboDetail.slaveItems.find(i => i == choiceItemId)) {
            return comboDetail.name;
          }
        }
      },

      async loadTags() {
        let tags = await this.getManyInventoryEntity({ entity: "tags", inventory: this.inventoryReference });
        return tags.map(t => ({ label: t.name, value: t.id }));
      }
    }
  }
</script>

<style lang="scss" scoped>
  #choiceItemEdit {
    display: flex;
  }

  .uploadImage {
    position: absolute;
    left: -999999px;
  }

  .image-preview {
    display: flex;
    height: 220px;
    border-radius: 5px;
    border: 1px solid #d9d9d9;
    background-image: url('../../../assets/img/photo-placeholder.png');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }

  .choice-explanation {
    font-size: 14px;
    margin-bottom: 30px;
  }
</style>